// src/hooks/useHealthMetrics.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HealthMetricsAPI } from "../api/healthMetrics";
import { format } from "date-fns";

const keyYM = (d) => (typeof d === "string" ? d : format(d, "yyyy-MM"));
const keyYMD = (d) => (typeof d === "string" ? d : format(d, "yyyy-MM-dd"));

/* ───────────── 혈압 ───────────── */
export function useBpMonth(monthDate) {
    return useQuery({
        queryKey: ["bp", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.bp.listMonth(keyYM(monthDate)),
    });
}

export function useBpDay(date) {
    return useQuery({
        queryKey: ["bp", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.bp.listDay(keyYMD(date)),
    });
}

export function useCreateBp() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.bp.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bp", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bp", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateBp() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.bp.update(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bp", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bp", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteBp() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.bp.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bp", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bp", "day", keyYMD(d)] });
        },
    });
}

/* ───────────── 혈당(공복) ───────────── */
export function useGlucoseMonth(monthDate) {
    return useQuery({
        queryKey: ["glucose", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.glucose.listMonth(keyYM(monthDate)),
    });
}

export function useGlucoseDay(date) {
    return useQuery({
        queryKey: ["glucose", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.glucose.listDay(keyYMD(date)),
    });
}

export function useCreateGlucose() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.glucose.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["glucose", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["glucose", "day", keyYMD(d)] });
        },
    });
}

/* ───────────── 체중 ───────────── */
export function useWeightMonth(monthDate) {
    return useQuery({
        queryKey: ["weight", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.weight.listMonth(keyYM(monthDate)),
    });
}

export function useWeightDay(date) {
    return useQuery({
        queryKey: ["weight", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.weight.listDay(keyYMD(date)),
    });
}

export function useCreateWeight() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.weight.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["weight", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["weight", "day", keyYMD(d)] });
        },
    });
}


/* ───────────── 추가: 수정/삭제 훅 ───────────── */
export function useUpdateWeight() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.weight.update(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["weight", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["weight", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteWeight() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.weight.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["weight", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["weight", "day", keyYMD(d)] });
        },
    });
}

export function useBodyFatMonth(monthDate) {
    return useQuery({
        queryKey: ["bodyFat", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.bodyFat.listMonth(keyYM(monthDate)),
    });
}

export function useBodyFatDay(date) {
    return useQuery({
        queryKey: ["bodyFat", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.bodyFat.listDay(keyYMD(date)),
    });
}

export function useCreateBodyFat() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.bodyFat.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bodyFat", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bodyFat", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateBodyFat() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.bodyFat.update(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bodyFat", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bodyFat", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteBodyFat() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.bodyFat.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["bodyFat", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["bodyFat", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateGlucose() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.glucose.update(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["glucose", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["glucose", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteGlucose() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.glucose.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["glucose", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["glucose", "day", keyYMD(d)] });
        },
    });
}


/* ───────────── 심폐지구력(cardio) ───────────── */
export function useCardioMonth(monthDate) {
    return useQuery({
        queryKey: ["cardio", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.cardio.listMonth(keyYM(monthDate)),
    });
}

export function useCardioDay(date) {
    return useQuery({
        queryKey: ["cardio", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.cardio.listDay(keyYMD(date)),
    });
}

export function useCreateCardio() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.cardio.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["cardio", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["cardio", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateCardio() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.cardio.update(payload),
        onSuccess: (_data, payload) => {
            // dt가 바뀌면 이전 날짜의 캐시도 비워주는 게 안전
            if (payload?.originalDt) {
                const od = new Date(payload.originalDt);
                qc.invalidateQueries({ queryKey: ["cardio", "month", keyYM(od)] });
                qc.invalidateQueries({ queryKey: ["cardio", "day", keyYMD(od)] });
            }
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["cardio", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["cardio", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteCardio() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.cardio.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["cardio", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["cardio", "day", keyYMD(d)] });
        },
    });
}



/* ───────────── 3종 근력 (triple-strength) ───────────── */
export function useTripleStrengthMonth(monthDate) {
    return useQuery({
        queryKey: ["tripleStrength", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.tripleStrength.listMonth(keyYM(monthDate)),
    });
}

export function useTripleStrengthDay(date) {
    return useQuery({
        queryKey: ["tripleStrength", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.tripleStrength.listDay(keyYMD(date)),
    });
}

export function useCreateTripleStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.tripleStrength.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["tripleStrength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["tripleStrength", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateTripleStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.tripleStrength.update(payload),
        onSuccess: (_data, payload) => {
            // 원본 dt가 따로 지정되면 그 날짜 캐시도 무효화
            if (payload?.originalDt) {
                const od = new Date(payload.originalDt);
                qc.invalidateQueries({ queryKey: ["tripleStrength", "month", keyYM(od)] });
                qc.invalidateQueries({ queryKey: ["tripleStrength", "day", keyYMD(od)] });
            }
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["tripleStrength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["tripleStrength", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteTripleStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.tripleStrength.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["tripleStrength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["tripleStrength", "day", keyYMD(d)] });
        },
    });
}

/* ───────────── 근력 (strength) ───────────── */
export function useStrengthMonth(monthDate) {
    return useQuery({
        queryKey: ["strength", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.strength.listMonth(keyYM(monthDate)),
    });
}

export function useStrengthDay(date) {
    return useQuery({
        queryKey: ["strength", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.strength.listDay(keyYMD(date)),
    });
}

export function useCreateStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.strength.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["strength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["strength", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.strength.update(payload),
        onSuccess: (_data, payload) => {
            // 원본 dt가 있으면 그 날짜 캐시도 무효화
            if (payload?.originalDt) {
                const od = new Date(payload.originalDt);
                qc.invalidateQueries({ queryKey: ["strength", "month", keyYM(od)] });
                qc.invalidateQueries({ queryKey: ["strength", "day", keyYMD(od)] });
            }
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["strength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["strength", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteStrength() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.strength.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["strength", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["strength", "day", keyYMD(d)] });
        },
    });
}



/* ───────────── 근파워 (power) ───────────── */
export function usePowerMonth(monthDate) {
    return useQuery({
        queryKey: ["power", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.power.listMonth(keyYM(monthDate)),
    });
}

export function usePowerDay(date) {
    return useQuery({
        queryKey: ["power", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.power.listDay(keyYMD(date)),
    });
}

export function useCreatePower() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.power.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["power", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["power", "day", keyYMD(d)] });
        },
    });
}

export function useUpdatePower() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.power.update(payload),
        onSuccess: (_data, payload) => {
            // 원본 날짜가 따로 있었다면 그 캐시도 무효화
            if (payload?.originalDt) {
                const od = new Date(payload.originalDt);
                qc.invalidateQueries({ queryKey: ["power", "month", keyYM(od)] });
                qc.invalidateQueries({ queryKey: ["power", "day", keyYMD(od)] });
            }
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["power", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["power", "day", keyYMD(d)] });
        },
    });
}

export function useDeletePower() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.power.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["power", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["power", "day", keyYMD(d)] });
        },
    });
}



/* ───────────── 근지구력 (muscular-endurance) ───────────── */
export function useMuscularEnduranceMonth(monthDate) {
    return useQuery({
        queryKey: ["muscularEndurance", "month", keyYM(monthDate)],
        queryFn: () => HealthMetricsAPI.muscularEndurance.listMonth(keyYM(monthDate)),
    });
}

export function useMuscularEnduranceDay(date) {
    return useQuery({
        queryKey: ["muscularEndurance", "day", keyYMD(date)],
        queryFn: () => HealthMetricsAPI.muscularEndurance.listDay(keyYMD(date)),
    });
}

export function useCreateMuscularEndurance() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.muscularEndurance.create(payload),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "day", keyYMD(d)] });
        },
    });
}

export function useUpdateMuscularEndurance() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => HealthMetricsAPI.muscularEndurance.update(payload),
        onSuccess: (_data, payload) => {
            // 기존 기록의 날짜가 따로 있었다면 그 캐시도 무효화
            if (payload?.originalDt) {
                const od = new Date(payload.originalDt);
                qc.invalidateQueries({ queryKey: ["muscularEndurance", "month", keyYM(od)] });
                qc.invalidateQueries({ queryKey: ["muscularEndurance", "day", keyYMD(od)] });
            }
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "day", keyYMD(d)] });
        },
    });
}

export function useDeleteMuscularEndurance() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dt }) => HealthMetricsAPI.muscularEndurance.delete({ id, dt }),
        onSuccess: (_data, payload) => {
            const d = payload?.dt ? new Date(payload.dt) : new Date();
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "month", keyYM(d)] });
            qc.invalidateQueries({ queryKey: ["muscularEndurance", "day", keyYMD(d)] });
        },
    });
}


export function useDashboardScore() {
    return useQuery({
        queryKey: ["dashboardScore"],
        queryFn: () => HealthMetricsAPI.dashboardScore.get(),
    });
}
